import { useTranslation } from 'react-i18next';
import { PLAN_DEFINITIONS, type Plan } from '@wrapforce/shared/constants';
import { useUpgradeDialogStore } from '@/stores/upgradeDialog';
import { useMembers } from '@/features/workspace/api';
import { useCheckout } from '@/features/billing/api';
import { useLocaleFormatters } from '@/lib/format';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const UPGRADE_PLANS: Plan[] = ['starter', 'pro', 'enterprise'];

export function UpgradeDialog({ workspaceId, role, open }: { workspaceId: string | null; role: string; open: boolean }) {
  const { t } = useTranslation('trial');
  const { formatMoney } = useLocaleFormatters();
  const { setOpen } = useUpgradeDialogStore();
  const { data: members = [] } = useMembers(workspaceId);
  const checkout = useCheckout(workspaceId);
  const isOwner = role === 'owner';
  const owner = members.find((m) => m.role === 'owner');

  async function handleUpgrade(plan: Plan) {
    const result = await checkout.mutateAsync({ plan: plan as 'starter' | 'pro' | 'enterprise', interval: 'month' });
    window.location.assign(result.url);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={t('upgradeDialog.title')}
      description={
        isOwner
          ? t('upgradeDialog.ownerDescription')
          : t('upgradeDialog.memberDescription', { name: owner?.fullName || owner?.email || t('upgradeDialog.fallbackOwnerName') })
      }
    >
      {isOwner ? (
        <div className="flex flex-col gap-2">
          {UPGRADE_PLANS.map((plan) => {
            const def = PLAN_DEFINITIONS[plan];
            return (
              <div key={plan} className="flex items-center justify-between rounded-[var(--radius-input)] border border-border p-3">
                <div>
                  <div className="text-sm font-bold text-ink">{t(`plan.${plan}.label`)}</div>
                  <div className="text-xs text-muted">
                    {def.priceMonthlyCents !== null ? `${formatMoney(def.priceMonthlyCents)}${t('upgradeDialog.perMonth')}` : ''} ·{' '}
                    {t('upgradeDialog.aiMockupsCount', { count: def.aiCreditsPerMonth })}
                  </div>
                </div>
                <Button size="sm" loading={checkout.isPending} onClick={() => handleUpgrade(plan)}>
                  {t('upgradeDialog.upgrade')}
                </Button>
              </div>
            );
          })}
        </div>
      ) : (
        <Button variant="outline" className="w-full" onClick={() => setOpen(false)}>
          {t('upgradeDialog.gotIt')}
        </Button>
      )}
    </Dialog>
  );
}
